import { useState } from "react";
import { ChevronRight } from "lucide-react";
import type { DataWarning } from "@/lib/api/assistantV2";

/**
 * Renders an answer's diagnostic `notes[]` (human-ready strings such as IC
 * stats), its per-answer `warnings`, and a collapsible raw `diagnostics` dump.
 */
export function AnswerNotes({
  notes,
  warnings,
  diagnostics,
}: {
  notes?: string[];
  warnings?: DataWarning[];
  diagnostics?: Record<string, unknown> | null;
}) {
  const [showDiag, setShowDiag] = useState(false);

  const hasNotes = notes != null && notes.length > 0;
  const hasWarnings = warnings != null && warnings.length > 0;
  const hasDiag = diagnostics != null && Object.keys(diagnostics).length > 0;

  if (!hasNotes && !hasWarnings && !hasDiag) return null;

  return (
    <div className="px-4 py-3 border-t border-slate-100 space-y-2">
      {hasNotes && (
        <ul className="text-xs text-slate-600 space-y-1">
          {notes!.map((n, i) => (
            <li key={i} className="flex gap-1.5">
              <span className="text-slate-400">•</span>
              <span className="font-mono">{n}</span>
            </li>
          ))}
        </ul>
      )}

      {hasWarnings && (
        <ul className="space-y-1">
          {warnings!.map((w, i) => (
            <li key={i} className="text-xs text-amber-700">
              <span className="font-mono text-[10px] uppercase mr-1.5 text-amber-600">
                {w.code}
              </span>
              {w.message}
            </li>
          ))}
        </ul>
      )}

      {hasDiag && (
        <div>
          <button
            type="button"
            onClick={() => setShowDiag((v) => !v)}
            className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-400 hover:text-slate-600"
          >
            <ChevronRight
              className={`h-3 w-3 transition-transform ${showDiag ? "rotate-90" : ""}`}
            />
            Diagnostics
          </button>
          {showDiag && (
            <pre className="mt-1 text-[11px] bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto text-slate-700">
              {JSON.stringify(diagnostics, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
